<template>
    <div class="modal-container">
        <form-wrapper class="modal-form" :validator="$v.rateDetail">
            <header class="modal-header">
                <div class="modal-header__right">
                    <md-button class="md-simple md-just-icon md-round" @click.stop="$modal.hide">
                        <md-icon>clear</md-icon>
                    </md-button>
                </div>
            </header>

            <section class="modal-section">
                <div class="section-override">
                    <md-switch v-model="rateDetail.overrideRule">
                       Override
                    </md-switch>
                </div>
                <div class="section-heading">
                    <span class="section-heading__badge">Timeframe</span>
                    <p class="section-heading__copy">Choose the active window for this rule.</p>
                </div>
                <div class="section-card section-card--wide">
                    <rule-valid-days
                        :current-days="rateDetail.validForWeekDays"
                        :from-date="rateDetail.validFromDate"
                        :to-date="rateDetail.validToDate"
                        @daysSelected="handleDaysSeleceted"
                        ref="ruleValidDays"
                    />
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Charges</span>
                    <p class="section-heading__copy">Set levy amounts and any related commissions.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="charging-grid">
                        <div class="charging-group">
                            <h5 class="charging-group__title">Fuel levy</h5>
                            <p class="charging-group__hint">Apply the primary levy percentage for this rule.</p>
                            <form-group name="fuelLevyPercentage" label="Fuel Levy Percentage">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.fuelLevyPercentage)"
                                    v-model="rateDetail.fuelLevyPercentage.operator"
                                    :value="rateDetail.fuelLevyPercentage.operator"
                                />
                                <md-input v-model.number="rateDetail.fuelLevyPercentage.value" />
                            </form-group>
                        </div>

                        <div class="charging-separator" aria-hidden="true"></div>

                        <div class="charging-group">
                            <h5 class="charging-group__title">Futile charge</h5>
                            <p class="charging-group__hint">Optional once-off amount when a job cannot be completed.</p>
                            <form-group name="futileCharge" label="Futile Charge">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.futileCharge)"
                                    v-model="rateDetail.futileCharge.operator"
                                    :value="rateDetail.futileCharge.operator"
                                />
                                <md-input type="number" v-model.number="rateDetail.futileCharge.value" />
                            </form-group>
                        </div>

                        <div class="charging-separator" aria-hidden="true" v-if="driverRuleCreation"></div>

                        <div class="charging-group" v-if="driverRuleCreation">
                            <h5 class="charging-group__title">Driver commission</h5>
                            <p class="charging-group__hint">Commission percentage applied when allocating to drivers.</p>
                            <form-group name="driverCommission" label="Commission Percentage">
                                <rule-operator
                                    v-if="$_nullChecker(rateDetail.driverCommission)"
                                    v-model="rateDetail.driverCommission.operator"
                                    :value="rateDetail.driverCommission.operator"
                                />
                                <md-input v-model.number="rateDetail.driverCommission.value" />
                            </form-group>
                        </div>
                    </div>
                </div>
            </section>

            <section class="modal-section">
                <div class="section-heading">
                    <span class="section-heading__badge">Coverage</span>
                    <p class="section-heading__copy">Control whether the levy automatically includes all surcharges.</p>
                </div>
                <div class="section-card section-card--compact">
                    <div class="switch-row">
                        <div class="switch-row__copy">
                            <h6 class="charging-group__title">Include all surcharges</h6>
                            <p class="charging-group__hint">Enable when fuel levy should cover any additional surcharges.</p>
                        </div>
                        <md-switch v-model="rateDetail.fuelLevyIncludesAllCharges">
                            Include all surcharges
                        </md-switch>
                    </div>
                </div>
            </section>

            <footer class="modal-footer">
                <div class="modal-footer__actions">
                    <md-button class="md-default dialog-button" @click.stop="$modal.hide">Cancel</md-button>
                    <md-button class="md-primary dialog-button" @click="create()">
                        {{ isUpdate ? 'Update' : 'Create' }}
                    </md-button>
                </div>
            </footer>
        </form-wrapper>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { decimal } from 'vuelidate/lib/validators';
import { RATE_RULE } from '@/utils/defaults';
import { GeneralMixin, RatesEngineMixin } from '@/mixins';
import { handleRequests, showErrorMessage } from '@/helpers';
import { RATE_CHARGING_TYPES } from '@/utils/constants';
import { RuleValidDays } from '@/components';
import RuleOperator from './RuleOperator';

export default {
    name: 'CreateLevyRuleContent',
    components: {
        RuleValidDays,
        RuleOperator
    },
    mixins: [GeneralMixin, RatesEngineMixin],
    props: {
        rateDetail: {
            type: Object,
            default: () => Object.assign({}, RATE_RULE()),
        },
        resolve: {
            type: Function,
            default: () => {}
        },
        listLength: {
            type: Number,
            default: 0
        },
        isUpdate: {
            type: Boolean,
            default: false
        },
        customerId: {
            type: Number,
            default: null
        },
        rateGroupId: {
            type: Number,
            default: null
        },
        driverUserId: {
            type: Number,
            default: null
        },
        driverRuleCreation: {
            type: Boolean,
            default: false
        },
        teamRegionId: {
            type: Number,
            default: null
        }
    },
    created() {
        this.$_ensureChargeField('fuelLevyPercentage');
        this.$_ensureChargeField('futileCharge');
        this.$_ensureChargeField('driverCommission');
    },
    data() {
        return {
            ruleData: {
                type: null,
                rule: 'test'
            },
            rateChargingTypes: RATE_CHARGING_TYPES,
            localMessages: {
                required: '{attribute} is required.'
            }
        };
    },
    validations: {
        rateDetail: {
            fuelLevyPercentage: {
                value: {
                    decimal
                }
            },
            futileCharge: {
                value: {
                    decimal
                }
            },
            driverCommission: {
                value: {
                    decimal
                }
            }
        }
    },
    computed: {
        ...mapGetters({
            user: 'user/user'
        })
    },
    methods: {
        validateData() {
            if (this.$v.$invalid) {
                return false;
            }

            const fuelLevyValue = this.$v.rateDetail.fuelLevyPercentage.value.$model;
            const futileChargeValue = this.$v.rateDetail.futileCharge.value.$model;
            const driverCommissionValidations = this.$v.rateDetail.driverCommission;
            const driverCommissionValue = driverCommissionValidations
                ? driverCommissionValidations.value.$model
                : null;

            if (fuelLevyValue === null && futileChargeValue === null && driverCommissionValue === null) {
                return false;
            }

            return true;
        },
        async create() {
            const ruleDaysValid = this.$refs.ruleValidDays.validate();
            if (!ruleDaysValid) {
                return;
            }

            if (this.isUpdate) {
                this.updateRuleDetails();
                return;
            }

            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            const fuelLevyValue = this.$v.rateDetail.fuelLevyPercentage.value.$model;
            this.rateDetail.fuelLevyEnabled = fuelLevyValue !== null;

            this.$_handleLoaderState(true, 'PROCESSING...');

            const chargeType = this.rateChargingTypes.find(
                (x) => x.key.toLowerCase() === this.$route.params.type.toLowerCase()
            );

            const obj = this.$_getRateRuleData(
                chargeType,
                this.user.publicUserId,
                this.rateDetail,
                this.customerId,
                this.listLength,
                this.rateGroupId,
                this.driverRuleCreation,
                this.driverUserId,
                this.teamRegionId
            );

            const endpoint = '/api/rates/create';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully created rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.data
                    });
                } else {
                    showErrorMessage(this, 'Failed creating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, error.data, null);
            }

            this.$_handleLoaderState(false);
        },
        async updateRuleDetails() {
            if (!this.validateData()) {
                showErrorMessage(this, 'Rule must have valid data', null);
                return;
            }

            this.$_handleLoaderState(true, 'UPDATING...');

            const rateId = this.rateDetail.rateRulesId;
            this.rateDetail.condition = null;
            this.rateDetail.currency = null;
            this.rateDetail.priority = null;
            this.rateDetail.rateRulesId = null;

            const obj = {
                rateRulesId: rateId,
                rule: JSON.stringify(this.rateDetail)
            };

            const endpoint = '/api/rates/updateRate';
            const payload = {
                method: 'post',
                data: obj
            };

            try {
                const response = await handleRequests(endpoint, payload);

                if (response.status === 200) {
                    this.$notify({
                        message: 'Successfully updated rule.',
                        type: 'success'
                    });

                    this.resolve({
                        record: response.status
                    });
                } else {
                    showErrorMessage(this, 'Failed updating rule.', null);
                }
            } catch (error) {
                showErrorMessage(this, 'Failed updating rule.', null);
            }

            this.$_handleLoaderState(false);
        },
        handleDaysSeleceted(value) {
            this.rateDetail = Object.assign(this.rateDetail, value);
        }
    }
};
</script>

<style lang="scss" scoped>
@import './create-rule-content.shared.scss';
</style>